//cta-5.twig

import React from 'react'
import {Section, Box, Text, Container} from '@miro-site/core'

import {
  contextualSpaces,
  Headline,
  Content,
  LazyImageAdapter,
  ButtonAdapter,
} from '@miro-site/components_utils'
import type {Assets} from '@miro-site/components_utils'
import {theme} from '@miro-site/design-tokens'
import styled from 'styled-components'

const WORKSHOPS_BG_COLORS = {
  'workshops-color-grey': theme.colors.workshopGray,
  'workshops-color-indigo': theme.colors.primaryIndigo,
  'workshops-color-blue': theme.colors.passionate,
  'workshops-color-pink': theme.colors.human,
  'workshops-color-red': theme.colors.bold,
  'workshops-color-yellow': theme.colors.outspoken,
} as const

type HeadlineType = React.ComponentProps<typeof Headline>
type BackgroundType = keyof typeof WORKSHOPS_BG_COLORS
interface CtaWithPatternProps {
  headline: HeadlineType
  content?: React.ComponentProps<typeof Content>
  background?: BackgroundType
  button: React.ComponentProps<typeof ButtonAdapter>
  additional_button?: React.ComponentProps<typeof ButtonAdapter>
  shape?: Assets
  button_text?: React.ComponentProps<typeof Content>
}

const highContrastBgColors: BackgroundType[] = [
  'workshops-color-indigo',
  'workshops-color-blue',
  'workshops-color-red',
]

const getHeadline = (headlineProps: HeadlineType, background: BackgroundType) => {
  const text =
    typeof headlineProps.text == 'string' ? headlineProps.text.trim() : headlineProps.text
  Object.assign(headlineProps, {
    text,
    size: headlineProps.size || 'mid',
    tag: headlineProps.tag || 'h2',
    color: highContrastBgColors.includes(background) ? 'white' : headlineProps.color,
    align: headlineProps.align || 'center',
  })

  return <Headline {...headlineProps}></Headline>
}

const Cta = styled(Box).attrs({
  overflow: 'hidden',
  $py: [96, 240],
  $px: [0],
})`
  position: relative;
  overflow: hidden;
  ${Text} {
    margin-left: auto;
    margin-right: auto;

    ${({theme}) => theme.normalizeStyles({maxWidth: [null, 645]})}
  }

  .cta-with-pattern {
    &__shape-wrapper {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      ${({theme}) =>
        theme.normalizeStyles({
          width: [365, 1430],
          height: [640, 1000],
        })}

      &--large {
        ${({theme}) =>
          theme.normalizeStyles({
            display: ['block'],
          })}
      }
    }

    &__shape {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);

      ${({theme}) =>
        theme.normalizeStyles({
          width: [365, 1430],
        })}
    }

    &__elements {
      text-align: center;
      position: relative;
      z-index: 3;
    }
  }
`

const CtaWithPattern: React.FC<CtaWithPatternProps> = ({
  headline,
  content,
  background = 'workshops-color-indigo',
  button,
  additional_button,
  shape,
  button_text,
}) => {
  const textColor = highContrastBgColors.includes(background)
    ? 'white'
    : background == 'workshops-color-grey'
    ? 'dark'
    : null
  return (
    <Section
      type="large"
      data-place="CTA Section"
      style={{paddingBottom: 0}}
      $styles={{textAlign: 'center'}}
    >
      <Cta
        $styles={{backgroundColor: WORKSHOPS_BG_COLORS[background]}}
        data-testid="cta-with-pattern_content"
      >
        <Container>
          <div className="cta-with-pattern__elements">
            {headline?.text && getHeadline({...headline, size: 'mid', tag: 'h2'}, background)}
            {content && (
              <Content
                {...content}
                textProps={{
                  $styles: {marginTop: contextualSpaces.textSpaces.afterHeadline.medium},
                }}
                color={textColor || content.color}
              ></Content>
            )}
            {(button || additional_button) && (
              <Box $styles={{marginTop: contextualSpaces.buttonSpaces.afterText.common}}>
                <ButtonAdapter
                  {...button}
                  type={background == 'workshops-color-grey' ? 'blue' : 'white'}
                ></ButtonAdapter>
                {additional_button && (
                  <ButtonAdapter
                    $styles={{marginLeft: [theme.spaces.md, theme.spaces.sm]}}
                    {...additional_button}
                    type={
                      highContrastBgColors.includes(background)
                        ? 'transparent-white'
                        : 'transparent'
                    }
                  ></ButtonAdapter>
                )}
              </Box>
            )}
            {button_text && (
              <Content
                {...button_text}
                textProps={{$styles: {marginTop: [theme.spaces.md]}}}
                size="tiny"
                color={textColor || button_text.color}
              ></Content>
            )}
          </div>
        </Container>
        {shape && (
          <div
            className={`cta-with-pattern__shape-wrapper ${
              shape.desktop ? 'cta-with-pattern__shape-wrapper--large' : ''
            }`}
          >
            <div className="cta-with-pattern__shape">
              <LazyImageAdapter assets={shape}></LazyImageAdapter>
            </div>
          </div>
        )}
      </Cta>
    </Section>
  )
}

export default CtaWithPattern
